import { Box, Paper, Stack, Typography } from "@mui/material";
import React from "react";

interface InternCardProps {
  title?: string;
  subtitle?: string;
  titleAction?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  variant?: "default" | "compact";
  noPadding?: boolean;
  /** Stretch to fill parent grid/flex cell */
  fullHeight?: boolean;
}

const InternCard = ({
  title,
  subtitle,
  titleAction,
  children,
  actions,
  variant = "default",
  noPadding = false,
  fullHeight = false,
}: InternCardProps) => {
  const isCompact = variant === "compact";

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: 2,
        overflow: "hidden",
        ...(fullHeight
          ? {
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }
          : null),
      }}
    >
      {(title || titleAction) && (
        <Box
          sx={{
            px: isCompact ? 2 : 2.5,
            py: isCompact ? 1.5 : 2,
            borderBottom: "1px solid var(--intern-border-default)",
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Box sx={{ minWidth: 0 }}>
              {title && (
                <Typography
                  variant={isCompact ? "subtitle1" : "h6"}
                  fontWeight={600}
                  sx={{ color: "var(--intern-text-primary)" }}
                >
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "var(--intern-text-tertiary)",
                    mt: 0.25,
                    fontSize: isCompact ? "0.75rem" : "0.8125rem",
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            {titleAction && <Box sx={{ flexShrink: 0 }}>{titleAction}</Box>}
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          px: noPadding ? 0 : isCompact ? 2 : 2.5,
          py: noPadding ? 0 : isCompact ? 1.5 : 2,
          ...(fullHeight
            ? {
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }
            : null),
        }}
      >
        {children}
      </Box>

      {actions && (
        <Box
          sx={{
            px: isCompact ? 2 : 2.5,
            py: isCompact ? 1.5 : 2,
            borderTop: "1px solid var(--intern-border-default)",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {actions}
        </Box>
      )}
    </Paper>
  );
};

export default InternCard;
